import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { schwabApi } from '@/lib/api/client';
import toast from 'react-hot-toast';

export const schwabKeys = {
  all: ['schwab'] as const,
  configStatus: () => [...schwabKeys.all, 'configStatus'] as const,
  status: () => [...schwabKeys.all, 'status'] as const,
  accounts: () => [...schwabKeys.all, 'accounts'] as const,
  syncHistory: () => [...schwabKeys.all, 'syncHistory'] as const,
  derivationStatus: () => [...schwabKeys.all, 'derivationStatus'] as const,
};

/** Query keys whose data a post-sync rebuild rewrites. Invalidated when the rebuild finishes so the
 * page corrects itself without a manual refresh. */
const REBUILD_AFFECTED_KEYS = [['chains'], ['stock-positions'], ['journal'], ['analytics']] as const;

/**
 * Polls the post-sync rebuild state while a rebuild is pending (every 15s; the cron drains one job
 * per 5-minute tick, so this is the granularity that matters), and stops polling once it clears.
 * On the pending → clear transition it invalidates the queries the rebuild rewrote.
 */
export function useDerivationStatus() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: schwabKeys.derivationStatus(),
    queryFn: () => schwabApi.getDerivationStatus(),
    refetchInterval: (q) => (q.state.data?.pending ? 15_000 : false),
  });

  const pending = query.data?.pending ?? false;
  const wasPending = useRef(false);
  useEffect(() => {
    if (wasPending.current && !pending) {
      for (const key of REBUILD_AFFECTED_KEYS) {
        queryClient.invalidateQueries({ queryKey: [...key] });
      }
    }
    wasPending.current = pending;
  }, [pending, queryClient]);

  return query;
}

export function useSchwabConfigStatus() {
  return useQuery({
    queryKey: schwabKeys.configStatus(),
    queryFn: () => schwabApi.getConfigStatus(),
    staleTime: 5 * 60 * 1000, // Config rarely changes, cache for 5 minutes
  });
}

export function useSchwabStatus() {
  return useQuery({
    queryKey: schwabKeys.status(),
    queryFn: () => schwabApi.getStatus(),
  });
}

export function useSchwabAccounts() {
  return useQuery({
    queryKey: schwabKeys.accounts(),
    queryFn: () => schwabApi.getAccounts(),
    select: (data) => data.accounts,
  });
}

export function useGetSchwabAuthUrl() {
  return useMutation({
    mutationFn: () => schwabApi.getAuthUrl(),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to get auth URL');
    },
  });
}

export function useSchwabCallback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (code: string) => schwabApi.callback(code),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: schwabKeys.all });
      queryClient.invalidateQueries({ queryKey: ['chains'] });
      toast.success('Connected to Schwab');

      const results = data.syncResults ?? [];
      if (results.length === 0) return;

      const totalOrders = results.reduce((sum, r) => sum + (r.ordersProcessed ?? 0), 0);
      const failed = results.filter((r) => !r.success);

      if (failed.length > 0) {
        toast.error(`Initial sync: ${failed.length} account(s) failed — ${failed[0].error ?? 'unknown error'}`);
      } else if (totalOrders === 0) {
        toast.success('Initial sync complete - no new orders found');
      } else {
        toast.success(`Initial sync complete: ${totalOrders} order${totalOrders > 1 ? 's' : ''} processed`);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to connect to Schwab');
    },
  });
}

export function useRefreshSchwabAccounts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => schwabApi.refreshAccounts(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: schwabKeys.all });
      if (data.accountsLinked > 0) {
        toast.success(`Found ${data.accountsLinked} new account(s)`);
      } else {
        toast.success('Accounts refreshed');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to refresh accounts');
    },
  });
}

export function useSyncSchwabAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accountId: string) => schwabApi.sync(accountId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: schwabKeys.all });
      // Also invalidate chains since new trades may have been imported
      queryClient.invalidateQueries({ queryKey: ['chains'] });

      // Show detailed results
      const result = data.results[0];
      if (result) {
        if (result.success) {
          const ordersProcessed = result.ordersProcessed ?? 0;
          const chainsCreated = result.chainsCreated ?? 0;
          const positionsCreated = result.positionsCreated ?? 0;
          const ordersFailed = result.ordersFailed ?? 0;

          if (ordersProcessed === 0) {
            toast.success('Sync complete - no new orders found');
          } else {
            const parts: string[] = [];
            if (positionsCreated > 0) parts.push(`${positionsCreated} position${positionsCreated > 1 ? 's' : ''} imported`);
            if (chainsCreated > 0) parts.push(`${chainsCreated} new trade${chainsCreated > 1 ? 's' : ''} created`);
            if (ordersFailed > 0) parts.push(`${ordersFailed} failed`);
            toast.success(`Sync complete: ${parts.join(', ') || `${ordersProcessed} orders processed`}`);
          }
        } else {
          toast.error(`Sync failed: ${result.error || 'Unknown error'}`);
        }
      } else {
        toast.success('Sync complete');
      }
    },
    onError: (error: Error) => {
      // A failed sync (e.g. session expired) changes connection state — refetch
      // the status so the badge flips instead of showing last-known Connected
      queryClient.invalidateQueries({ queryKey: schwabKeys.status() });
      toast.error(error.message || 'Failed to sync');
    },
  });
}

export function useSyncHistory() {
  return useQuery({
    queryKey: schwabKeys.syncHistory(),
    queryFn: () => schwabApi.syncHistory(20),
    select: (data) => data.logs,
  });
}

export function useDisconnectSchwab() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => schwabApi.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schwabKeys.all });
      toast.success('Disconnected from Schwab');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to disconnect');
    },
  });
}
